/**
 * React Query Hooks fuer Channel Readiness Dashboard
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getReadinessSummary,
  getReadinessProducts,
  getReadinessIssues,
  getProductReadiness,
  triggerRecompute,
} from '@/lib/api/channel-readiness'

export function useReadinessSummary() {
  return useQuery({
    queryKey: ['readiness', 'summary'],
    queryFn: getReadinessSummary,
    select: (data) => data.summary,
    staleTime: 30_000,
  })
}

export function useReadinessProducts(
  channelId: string | null,
  params?: {
    page?: number
    page_size?: number
    min_score?: number
    max_score?: number
    search?: string
    issue_type?: string
    issue_field?: string
    issue_message?: string
    missing_packshot?: boolean
    image_status?: string
    sort_by?: string
    sort_dir?: string
  }
) {
  return useQuery({
    queryKey: ['readiness', 'products', channelId, params],
    queryFn: () => getReadinessProducts(channelId!, params),
    enabled: !!channelId,
    staleTime: 30_000,
  })
}

export function useReadinessIssues(channelId: string | null) {
  return useQuery({
    queryKey: ['readiness', 'issues', channelId],
    queryFn: () => getReadinessIssues(channelId!),
    enabled: !!channelId,
    select: (data) => ({
      issues: data.issues,
      missing_packshot_count: data.missing_packshot_count ?? 0,
      image_intelligence: data.image_intelligence ?? null,
    }),
    staleTime: 30_000,
  })
}

export function useProductReadiness(productId: string | null) {
  return useQuery({
    queryKey: ['readiness', 'product', productId],
    queryFn: () => getProductReadiness(productId!),
    enabled: !!productId,
    select: (data) => data.scores,
  })
}

export function useRecompute() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: triggerRecompute,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readiness'] })
    },
  })
}
